import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getSocket } from "@/lib/socket";
import { fmtDateTime } from "@/lib/time";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const socketRef = useRef(null);

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    const s = getSocket();
    socketRef.current = s;
    const onNotif = (n) => {
      setItems((prev) => [n, ...prev]);
      toast(n.message, { description: "Nuova notifica" });
      // Notifica desktop del sistema operativo (se permesso)
      if ("Notification" in window && Notification.permission === "granted") {
        try {
          new Notification(`Ticket ${n.ticket_code || ""}`.trim(), { body: n.message, icon: "/logo192.png", tag: n.id });
        } catch (e) { /* ignore */ }
      }
    };
    s.on("notification", onNotif);
    return () => { s.off("notification", onNotif); };
  }, [load]);

  const unread = items.filter((n) => !n.read).length;
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  const openItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    load();
    if (n.ticket_id) navigate(`/tickets/${n.ticket_id}`);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative rounded-lg border border-border bg-card p-2 text-muted-foreground transition-colors hover:text-foreground" data-testid="notif-bell">
          <Bell size={18} />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white" data-testid="notif-count">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <p className="font-bold text-navy">Notifiche</p>
          {unread > 0 && <button onClick={readAll} className="flex items-center gap-1 text-xs font-semibold text-primary" data-testid="notif-read-all"><CheckCheck size={14} />Segna lette</button>}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length ? items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} className={cn("flex w-full flex-col items-start gap-0.5 border-b border-border/60 px-4 py-3 text-left last:border-0 hover:bg-secondary/50", !n.read && "bg-blue-50/50")} data-testid={`notif-item-${n.id}`}>
              <span className="text-sm text-navy">{n.message}</span>
              <span className="text-[11px] text-muted-foreground">{fmtDateTime(n.created_at)}</span>
            </button>
          )) : <p className="px-4 py-8 text-center text-sm text-muted-foreground">Nessuna notifica</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
